import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Gavel, HelpCircle, ArrowLeft, Lock, Phone, Mail, User, CheckCircle, Loader2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Switch } from "@/components/ui/switch";
import { trackFormSubmission } from "@/lib/gtag";
import { submitLead, isLikelyBot } from "@/lib/crm";
import { useLocalePath } from "@/components/LocalLink";
import PhoneField from "@/components/PhoneField";

type CaseCategory = "immigration" | "criminal" | "other" | null;

interface FormData {
  category: CaseCategory;
  subCategory: string;
  otherDescription: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  preferWhatsApp: boolean;
  preferredLanguage: string;
  consentToContact: boolean;
}

const initialFormData: FormData = {
  category: null,
  subCategory: "",
  otherDescription: "",
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  preferWhatsApp: false,
  preferredLanguage: "",
  consentToContact: false,
};

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 80 : -80,
    opacity: 0,
  }),
  center: { x: 0, opacity: 1 },
  exit: (direction: number) => ({
    x: direction > 0 ? -80 : 80,
    opacity: 0,
  }),
};

const SmartCaseEvaluation = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const localePath = useLocalePath();
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [honeypot, setHoneypot] = useState("");
  const [formStartedAt] = useState(() => Date.now());

  const progressValue = step === 1 ? 33 : step === 2 ? 66 : 100;

  const goNext = () => {
    setDirection(1);
    setStep((s) => Math.min(s + 1, 3));
  };

  const goBack = () => {
    setDirection(-1);
    setStep((s) => Math.max(s - 1, 1));
  };

  const selectCategory = (cat: CaseCategory) => {
    setFormData((d) => ({ ...d, category: cat, subCategory: "", otherDescription: "" }));
    goNext();
  };

  const selectSubCategory = (sub: string) => {
    setFormData((d) => ({ ...d, subCategory: sub }));
  };

  const handleSubmit = async () => {
    setSubmitStatus("loading");

    if (isLikelyBot(honeypot, formStartedAt)) {
      setSubmitStatus("success");
      return;
    }

    const categoryLabelMap: Record<string, string> = {
      immigration: "Immigration",
      criminal: "Criminal Defense",
      other: "Other",
    };

    const caseCategory = formData.category ? categoryLabelMap[formData.category] : "";
    const caseSubcategory = formData.category === "other" ? "Other" : formData.subCategory;
    const situationDescription = formData.category === "other" ? formData.otherDescription : "";

    const ok = await submitLead({
      form_name: "smart_case_evaluation",
      first_name: formData.firstName.trim(),
      last_name: formData.lastName.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      preferred_language: formData.preferredLanguage || language,
      prefers_whatsapp: String(formData.preferWhatsApp),
      case_category: caseCategory,
      case_subcategory: caseSubcategory,
      situation_description: situationDescription,
      consent_to_contact: String(formData.consentToContact),
    });

    if (ok) {
      setSubmitStatus("success");
      trackFormSubmission("smart_case_evaluation");
      navigate(localePath("/thank-you"));
    } else {
      setSubmitStatus("error");
    }
  };

  const categoryCards = [
    { id: "immigration" as const, icon: Globe, labelKey: "wizard.category.immigration", descKey: "wizard.category.immigration.desc" },
    { id: "criminal" as const, icon: Gavel, labelKey: "wizard.category.criminal", descKey: "wizard.category.criminal.desc" },
    { id: "other" as const, icon: HelpCircle, labelKey: "wizard.category.other", descKey: "wizard.category.other.desc" },
  ];

  const immigrationOptions = [
    { key: "wizard.option.residence", value: "Residence / Green Card" },
    { key: "wizard.option.citizenship", value: "Citizenship" },
    { key: "wizard.option.visa", value: "Visa" },
  ];

  const criminalOptions = [
    { key: "wizard.option.dui", value: "DUI / DWI" },
    { key: "wizard.option.drug", value: "Drug Offense" },
    { key: "wizard.option.dv", value: "Domestic Violence" },
    { key: "wizard.option.assault", value: "Assault" },
    { key: "wizard.option.other", value: "Other" },
  ];

  return (
    <div className="w-full max-w-lg mx-auto">
      {/* Glassmorphism Card */}
      <div className="rounded-2xl bg-white/95 backdrop-blur-xl shadow-[0_8px_40px_-12px_rgba(0,0,0,0.25)] border border-white/60 overflow-hidden">
        {/* Progress Bar */}
        <div className="px-6 pt-6 pb-2">
          <div className="flex items-center justify-between text-xs font-medium text-navy/50 mb-2">
            <span>{t("wizard.step")} {step} {t("wizard.of")} 3</span>
            <span>{progressValue}%</span>
          </div>
          <div className="h-2 rounded-full bg-navy/10 overflow-hidden">
            <motion.div
              className="h-full rounded-full"
              style={{ background: "var(--gold-gradient)" }}
              animate={{ width: `${progressValue}%` }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
            />
          </div>
        </div>

        {/* Steps Container */}
        <div className="p-6 min-h-[580px] relative overflow-hidden">
          <AnimatePresence mode="wait" custom={direction}>
            {step === 1 && (
              <motion.div
                key="step1"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                {/* Step 1: Triage */}
                <div>
                  <h3 className="text-xl font-heading font-bold text-navy mb-1">
                    {t("wizard.step1.title")}
                  </h3>
                  <p className="text-navy/50 text-sm mb-6">
                    {t("wizard.step1.subtitle")}
                  </p>
                  <div className="grid gap-3">
                    {categoryCards.map((card) => (
                      <button
                        key={card.id}
                        onClick={() => selectCategory(card.id)}
                        className="flex items-center gap-4 p-4 rounded-xl border border-navy/10 bg-white hover:border-gold hover:shadow-md transition-all text-left group"
                      >
                        <div className="w-12 h-12 rounded-lg bg-navy/5 flex items-center justify-center group-hover:bg-gold/10 transition-colors shrink-0">
                          <card.icon className="w-5 h-5 text-navy/60 group-hover:text-gold transition-colors" />
                        </div>
                        <div>
                          <p className="font-semibold text-navy text-sm">{t(card.labelKey)}</p>
                          <p className="text-navy/40 text-xs">{t(card.descKey)}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="step2"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                {/* Step 2: Specifics */}
                <div>
                  <h3 className="text-xl font-heading font-bold text-navy mb-1">
                    {formData.category === "immigration"
                      ? t("wizard.step2.immigration.title")
                      : formData.category === "criminal"
                        ? t("wizard.step2.criminal.title")
                        : t("wizard.step2.other.title")}
                  </h3>
                  <p className="text-navy/50 text-sm mb-6">
                    {formData.category === "other" ? t("wizard.step2.other.subtitle") : t("wizard.step2.select.subtitle")}
                  </p>

                  {formData.category === "other" ? (
                    <textarea
                      value={formData.otherDescription}
                      onChange={(e) => setFormData((d) => ({ ...d, otherDescription: e.target.value }))}
                      rows={4}
                      className="w-full rounded-xl border border-navy/15 bg-white px-4 py-3 text-sm text-navy placeholder:text-navy/30 focus:outline-none focus:ring-2 focus:ring-gold/40 resize-none"
                      placeholder={t("wizard.step2.other.placeholder")}
                    />
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      {(formData.category === "immigration" ? immigrationOptions : criminalOptions).map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => selectSubCategory(opt.value)}
                          className={`p-3 rounded-xl border text-sm font-medium transition-all text-center ${
                            formData.subCategory === opt.value
                              ? "border-gold bg-gold/10 text-navy shadow-sm"
                              : "border-navy/10 bg-white text-navy/70 hover:border-gold/50"
                          }`}
                        >
                          {t(opt.key)}
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between mt-8">
                    <button onClick={goBack} className="flex items-center gap-1 text-sm text-navy/50 hover:text-navy transition-colors">
                      <ArrowLeft className="w-4 h-4" /> {t("wizard.back")}
                    </button>
                    <button
                      onClick={goNext}
                      disabled={formData.category === "other" ? formData.otherDescription.trim().length === 0 : formData.subCategory === ""}
                      className="btn-gold px-6 py-2.5 rounded-lg text-sm font-semibold text-navy disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    >
                      {t("wizard.next")}
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                key="step3"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                {/* Step 3: Contact Info */}
                <div>
                  {submitStatus === "success" ? (
                    <div className="flex flex-col items-center justify-center text-center py-8">
                      <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                        <CheckCircle className="w-8 h-8 text-green-600" />
                      </div>
                      <h3 className="text-xl font-heading font-bold text-navy mb-2">
                        {t("wizard.success.title")}
                      </h3>
                      <p className="text-navy/60 text-sm">
                        {t("wizard.success.message")}
                      </p>
                    </div>
                  ) : submitStatus === "error" ? (
                    <div className="flex flex-col items-center justify-center text-center py-8">
                      <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
                        <Phone className="w-8 h-8 text-red-600" />
                      </div>
                      <h3 className="text-xl font-heading font-bold text-navy mb-2">
                        {t("wizard.error.title")}
                      </h3>
                      <p className="text-navy/60 text-sm mb-4">
                        {t("wizard.error.message")}
                      </p>
                      <button
                        onClick={() => setSubmitStatus("idle")}
                        className="text-sm text-gold hover:underline font-medium"
                      >
                        Try again
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* Honeypot */}
                      <div className="hidden" tabIndex={-1}>
                        <input
                          type="text"
                          name="website"
                          autoComplete="off"
                          value={honeypot}
                          onChange={(e) => setHoneypot(e.target.value)}
                          tabIndex={-1}
                        />
                      </div>

                      <h3 className="text-xl font-heading font-bold text-navy mb-1">
                        {t("wizard.step3.title")}
                      </h3>
                      <p className="text-navy/50 text-sm mb-6">{t("wizard.step3.subtitle")}</p>

                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/30" />
                            <input
                              type="text"
                              value={formData.firstName}
                              onChange={(e) => setFormData((d) => ({ ...d, firstName: e.target.value }))}
                              placeholder="First Name"
                              className="w-full pl-10 pr-4 py-3 rounded-xl border border-navy/15 bg-white text-sm text-navy placeholder:text-navy/30 focus:outline-none focus:ring-2 focus:ring-gold/40"
                            />
                          </div>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/30" />
                            <input
                              type="text"
                              value={formData.lastName}
                              onChange={(e) => setFormData((d) => ({ ...d, lastName: e.target.value }))}
                              placeholder="Last Name"
                              className="w-full pl-10 pr-4 py-3 rounded-xl border border-navy/15 bg-white text-sm text-navy placeholder:text-navy/30 focus:outline-none focus:ring-2 focus:ring-gold/40"
                            />
                          </div>
                        </div>
                        <PhoneField
                          value={formData.phone}
                          onChange={(e164) => setFormData((d) => ({ ...d, phone: e164 }))}
                          placeholder={t("wizard.placeholder.phone")}
                          className="w-full px-4 py-3 rounded-xl border border-navy/15 bg-white text-sm text-navy placeholder:text-navy/30 focus:outline-none focus:ring-2 focus:ring-gold/40"
                        />
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/30" />
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData((d) => ({ ...d, email: e.target.value }))}
                            placeholder={t("wizard.placeholder.email")}
                            className="w-full pl-10 pr-4 py-3 rounded-xl border border-navy/15 bg-white text-sm text-navy placeholder:text-navy/30 focus:outline-none focus:ring-2 focus:ring-gold/40"
                          />
                        </div>

                        {/* Preferred Language Dropdown */}
                        <div className="relative">
                          <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/30 pointer-events-none" />
                          <select
                            value={formData.preferredLanguage}
                            onChange={(e) => setFormData((d) => ({ ...d, preferredLanguage: e.target.value }))}
                            required
                            className="w-full pl-10 pr-4 py-3 rounded-xl border border-navy/15 bg-white text-sm text-navy focus:outline-none focus:ring-2 focus:ring-gold/40 appearance-none cursor-pointer"
                            style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23999'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center", backgroundSize: "1.25rem" }}
                          >
                            <option value="" disabled>{t("wizard.preferredLanguage.placeholder")}</option>
                            <option value="EN">English</option>
                            <option value="ES">Español</option>
                            <option value="PT">Português</option>
                          </select>
                        </div>

                        {/* WhatsApp Toggle */}
                        <div className="flex items-center justify-between p-3 rounded-xl bg-navy/[0.03] border border-navy/10">
                          <span className="text-sm text-navy/70">{t("wizard.whatsapp.label")}</span>
                          <Switch
                            checked={formData.preferWhatsApp}
                            onCheckedChange={(v) => setFormData((d) => ({ ...d, preferWhatsApp: v }))}
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-between mt-8">
                        <button onClick={goBack} className="flex items-center gap-1 text-sm text-navy/50 hover:text-navy transition-colors">
                          <ArrowLeft className="w-4 h-4" /> {t("wizard.back")}
                        </button>
                        <button
                          onClick={handleSubmit}
                          disabled={!(formData.firstName?.trim() && formData.lastName?.trim() && formData.phone?.trim() && formData.email?.trim() && formData.preferredLanguage && formData.consentToContact) || submitStatus === "loading"}
                          className="flex-1 ml-4 btn-gold py-3 rounded-lg text-sm font-bold text-navy disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                        >
                          {submitStatus === "loading" ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            t("wizard.submit")
                          )}
                        </button>
                      </div>

                      {/* Consent Checkbox */}
                      <label className="flex items-start gap-3 mt-4 p-3 rounded-lg bg-navy/[0.03] border border-navy/10 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.consentToContact}
                          onChange={(e) => setFormData((d) => ({ ...d, consentToContact: e.target.checked }))}
                          className="mt-0.5 w-4 h-4 rounded border-navy/30 text-gold focus:ring-gold/40 accent-gold cursor-pointer"
                        />
                        <span className="text-xs text-navy/70 leading-relaxed select-none">
                          {t("wizard.consent")}
                        </span>
                      </label>


                      {/* Confidentiality Footer */}
                      <div className="flex items-center justify-center gap-1.5 mt-4 text-xs text-navy/35">
                        <Lock className="w-3 h-3" />
                        <span>{t("wizard.confidential")}</span>
                      </div>
                    </>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default SmartCaseEvaluation;
